import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Modal } from "../../../components/modal/Modal"
import { getAdminLessonApi, getLessonApi, AdminLessonDetail } from "../../lessons/api/lesson.api"
import { LessonMapEntry } from "../hooks/useCourseLessonMap"
import {
  FilmIcon,
  DocumentTextIcon,
  ClockIcon,
  ArrowTopRightOnSquareIcon,
  FunnelIcon,
} from "@heroicons/react/16/solid"

type Props = {
  lessonId: string | null
  lessonMap: Record<string, LessonMapEntry>
  onClose: () => void
  onFilter: (lessonId: string) => void
}

/** Slide-in preview opened from a lesson tag in chat — shows the lesson and lets a mentor jump straight to it. */
export const LessonPreviewDrawer = ({ lessonId, lessonMap, onClose, onFilter }: Props) => {
  const navigate = useNavigate()
  const isAdmin = localStorage.getItem("role") === "admin"
  const [lesson, setLesson] = useState<AdminLessonDetail | null>(null)
  const [loading, setLoading] = useState(false)

  const entry = lessonId ? lessonMap[lessonId] : null

  useEffect(() => {
    if (!lessonId) {
      setLesson(null)
      return
    }

    setLoading(true)
    setLesson(null)

    const moduleId = lessonMap[lessonId]?.moduleId
    const request = isAdmin
      ? getAdminLessonApi(lessonId).then((res) => res.data)
      : moduleId
        ? getLessonApi(moduleId, lessonId).then((res) => ({
            id: res.data.id,
            module_id: res.data.module_id,
            title: res.data.title,
            type: res.data.type,
            access_type: res.data.access_type,
            content: res.data.content,
            video_id: null as string | null,
            duration: res.data.duration,
            order_number: res.data.order_number,
            is_active: res.data.is_active ?? true,
          }))
        : Promise.reject(new Error("missing moduleId"))

    request
      .then((data) => setLesson(data))
      .catch(() => setLesson(null))
      .finally(() => setLoading(false))
  }, [lessonId, isAdmin, lessonMap])

  const goToLesson = () => {
    if (!lessonId) return
    const moduleId = lesson?.module_id ?? entry?.moduleId
    if (!moduleId) return
    navigate(`/modules/${moduleId}/lessons/${lessonId}`)
  }

  return (
    <Modal open={!!lessonId} onClose={onClose} title={lesson?.title ?? entry?.title ?? "Урок"}>
      {loading ? (
        <div className="space-y-3 animate-pulse">
          <div className="h-4 bg-gray-100 rounded w-1/2" />
          <div className="h-20 bg-gray-100 rounded" />
        </div>
      ) : (
        <div className="space-y-4">
          {entry?.moduleTitle && (
            <p className="text-xs text-gray-400">
              Модуль: <span className="text-gray-600 font-medium">{entry.moduleTitle}</span>
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 ring-1 ring-blue-200">
              {lesson?.type === "video" ? <FilmIcon className="w-3.5 h-3.5" /> : <DocumentTextIcon className="w-3.5 h-3.5" />}
              {lesson?.type === "video" ? "Видео" : "Текст"}
            </span>
            {lesson?.duration && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                <ClockIcon className="w-3.5 h-3.5" />
                {lesson.duration}
              </span>
            )}
          </div>

          {lesson?.content ? (
            <div className="text-sm text-gray-600 leading-relaxed line-clamp-6 whitespace-pre-wrap">
              {lesson.content}
            </div>
          ) : (
            <p className="text-sm text-gray-400 italic">Нет содержимого</p>
          )}

          <div className="pt-2 space-y-2">
            <button
              onClick={goToLesson}
              className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-xl text-sm font-semibold transition-colors shadow-sm shadow-blue-200"
            >
              <ArrowTopRightOnSquareIcon className="w-4 h-4" />
              Перейти к уроку
            </button>
            <button
              onClick={() => lessonId && onFilter(lessonId)}
              className="w-full flex items-center justify-center gap-2 border border-gray-200 text-gray-600 hover:bg-gray-50 py-2.5 rounded-xl text-sm font-medium transition-colors"
            >
              <FunnelIcon className="w-4 h-4" />
              Показать только вопросы по этому уроку
            </button>
          </div>
        </div>
      )}
    </Modal>
  )
}
